import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { hocSinhApi } from "../../../api/hocSinhApi";
import { healthProblemApi } from "../../../api/y-te/healthProblemApi";
import { toApiLanguage } from "../../../helpers/apiLanguage";
import { IBaseResponeModel } from "../../../models/response/base-response";
import {
	eStudentMedicalHistoryActionTypeIds,
	IStudentMedicalHistoryDeleteStart,
	IStudentMedicalHistoryLoadByHocSinhStart,
	IStudentMedicalHistoryLoadExportStart,
	IStudentMedicalHistoryLoadHocSinhStart,
	IStudentMedicalHistoryLoadStart,
	IStudentMedicalHistorySaveStart
} from "../../action-types/y-te/IStudentMedicalHistoryActionTypes";
import { studentMedicalHistoryActions } from "../../actions/y-te/studentMedicalHistoryActions";

export function* studentMedicalHistorySaga(): any {
	yield takeLatest(eStudentMedicalHistoryActionTypeIds.LOAD_START, loadStudentMedicalHistoryWorker)
	yield takeLatest(eStudentMedicalHistoryActionTypeIds.LOAD_EXPORT_START, loadStudentMedicalHistoryExportWorker)
	yield takeLatest(eStudentMedicalHistoryActionTypeIds.LOAD_HOC_SINH_START, loadHocSinhWorker)
	yield takeLatest(eStudentMedicalHistoryActionTypeIds.LOAD_BY_HOC_SINH_START, loadHealthProblemByHocSinhWorker)
	yield takeEvery(eStudentMedicalHistoryActionTypeIds.SAVE_START, saveStudentMedicalHistoryWorker)
	yield takeEvery(eStudentMedicalHistoryActionTypeIds.DELETE_START, deleteStudentMedicalHistoryWorker)
}

function* loadStudentMedicalHistoryWorker(action: IStudentMedicalHistoryLoadStart): any {
	const { dm_coso_id, nam_hoc } = action.payload;
	const res: IBaseResponeModel = yield call([healthProblemApi, healthProblemApi.SelectStudentMedicalHistory], dm_coso_id, nam_hoc)
	if (res.is_success) {
		yield put(studentMedicalHistoryActions.loadStudentMedicalHistorySuccess(res.data || []))
	} else {
		yield put(studentMedicalHistoryActions.loadStudentMedicalHistoryError(res.message || "Không tải được danh sách tiền sử bệnh học sinh"))
	}
}

function* loadStudentMedicalHistoryExportWorker(action: IStudentMedicalHistoryLoadExportStart): any {
	const { dm_coso_id, nam_hoc, language } = action.payload;
	const res: IBaseResponeModel = yield call(
		[healthProblemApi, healthProblemApi.SelectStudentMedicalHistoryDetail],
		dm_coso_id,
		nam_hoc,
		toApiLanguage(language)
	)
	if (res.is_success) {
		yield put(studentMedicalHistoryActions.loadStudentMedicalHistoryExportSuccess(res.data || []))
	} else {
		yield put(studentMedicalHistoryActions.loadStudentMedicalHistoryExportError(res.message || "Không tải được dữ liệu xuất Excel"))
	}
}

/** adm gọi `hocSinhApi.Detail(id, nam_hoc)` (`api/hoc-sinh/{id}/nam-hoc/{nam_hoc}`) — backend sis
 *  không có route đó, dùng `api/hoc-sinh/{id}` (`hocSinhApi.SelectById`) vì trang chỉ cần ma_hs + ho_ten. */
function* loadHocSinhWorker(action: IStudentMedicalHistoryLoadHocSinhStart): any {
	const res: IBaseResponeModel = yield call([hocSinhApi, hocSinhApi.SelectById], action.payload)
	if (res.is_success) {
		yield put(studentMedicalHistoryActions.loadStudentMedicalHistoryHocSinhSuccess({
			ts_hocsinh_id: res.data?.id ?? action.payload,
			ma_hs: res.data?.ma_hs ?? '',
			ts_hocsinh_hoten: res.data?.ho_ten ?? ''
		}))
	} else {
		yield put(studentMedicalHistoryActions.loadStudentMedicalHistoryHocSinhError(res.message || "Không tải được thông tin học sinh"))
	}
}

function* loadHealthProblemByHocSinhWorker(action: IStudentMedicalHistoryLoadByHocSinhStart): any {
	const { ts_hocsinh_id, nam_hoc } = action.payload;
	const res: IBaseResponeModel = yield call([healthProblemApi, healthProblemApi.SelectByHocSinh], ts_hocsinh_id, nam_hoc)
	if (res.is_success) {
		yield put(studentMedicalHistoryActions.loadHealthProblemByHocSinhSuccess(res.data || []))
	} else {
		yield put(studentMedicalHistoryActions.loadHealthProblemByHocSinhError(res.message || "Không tải được tiền sử bệnh của học sinh"))
	}
}

/**
 * Lưu tiền sử bệnh: XOÁ HẾT bản ghi của (học sinh, năm học) rồi INSERT lại toàn bộ danh sách.
 *
 * LƯU Ý: backend `POST api/health-problem/student-medical-history` chỉ insert (không xoá,
 * không upsert), nên bắt buộc phải xoá trước, nếu không mỗi lần lưu sẽ nhân đôi dữ liệu.
 * Hai bước này KHÔNG nằm trong cùng transaction (backend không có API gộp): nếu bước insert
 * lỗi sau khi delete đã thành công thì dữ liệu cũ đã mất — giữ đúng như adm, không tự thêm
 * transaction ở tầng client.
 */
function* saveStudentMedicalHistoryWorker(action: IStudentMedicalHistorySaveStart): any {
	const { ts_hocsinh_id, nam_hoc } = action.payload;
	const resDelete: IBaseResponeModel = yield call([healthProblemApi, healthProblemApi.DeleteByHocSinh], ts_hocsinh_id, nam_hoc)
	if (!resDelete.is_success) {
		yield put(studentMedicalHistoryActions.saveStudentMedicalHistoryError(resDelete.message || "Không xoá được tiền sử bệnh cũ của học sinh"))
		return;
	}
	const res: IBaseResponeModel = yield call([healthProblemApi, healthProblemApi.InsertStudentMedicalHistory], action.payload)
	if (res.is_success) {
		yield put(studentMedicalHistoryActions.saveStudentMedicalHistorySuccess())
	} else {
		yield put(studentMedicalHistoryActions.saveStudentMedicalHistoryError(res.message || "Không lưu được tiền sử bệnh của học sinh"))
	}
}

function* deleteStudentMedicalHistoryWorker(action: IStudentMedicalHistoryDeleteStart): any {
	const { ts_hocsinh_id, nam_hoc } = action.payload;
	const res: IBaseResponeModel = yield call([healthProblemApi, healthProblemApi.DeleteByHocSinh], ts_hocsinh_id, nam_hoc)
	if (res.is_success) {
		yield put(studentMedicalHistoryActions.deleteStudentMedicalHistorySuccess())
	} else {
		yield put(studentMedicalHistoryActions.deleteStudentMedicalHistoryError(res.message || "Không xoá được tiền sử bệnh của học sinh"))
	}
}
